/* eslint-disable no-console */
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { useFetch } from '@osmosis/nextjs-components/context';
import { getDeviceID } from '../lib/util/tracking';
import { useQuery, useQueryClient } from 'react-query';
import { useVisitorData } from '@fingerprint/react';
import {
  FeatureFlagsSet,
  useFeatureFlagsContext,
} from '@osmosis/nextjs-components/context/FeatureFlagsContext';

const TRIAL_METADATA_SESSION_KEY = 'trialMetadataPosted';

// The gate mounts on every page, so dedupe per browser session: the evaluation
// is deterministic for an unchanged user/device, and re-running it costs a
// geolocation lookup plus the full match query on the server.
const readPostedKey = (): string | null => {
  try {
    return window.sessionStorage.getItem(TRIAL_METADATA_SESSION_KEY);
  } catch {
    return null;
  }
};

const writePostedKey = (key: string): void => {
  try {
    window.sessionStorage.setItem(TRIAL_METADATA_SESSION_KEY, key);
  } catch {
    // Storage unavailable (private mode): fall back to posting on each load.
  }
};

/**
 * Hook to update trial metadata.
 */
const useUpdateTrialMetadata = () => {
  const { user, hasFreeTrialPrime, hasExpiredPrime } = useUserContext();
  const { post, get } = useFetch();
  const queryClient = useQueryClient();

  const { isActive } = useFeatureFlagsContext();
  const { getData: getFingerprintData } = useVisitorData({ immediate: false });

  const fingerprintEnabled = isActive(FeatureFlagsSet.fingerprintEnabled);

  const hasStoredFingerprintData = async (): Promise<boolean> => {
    if (!fingerprintEnabled) return false;

    try {
      const { data } = await get<{ visitorID: string }>('/api/getVisitorID');
      return !!data?.visitorID;
    } catch {
      return false;
    }
  };

  useQuery(
    `user-trial-metadata`,
    async () => {
      try {
        const deviceID = await getDeviceID();
        const postedKey = `${user?.userID}:${deviceID ?? ''}`;
        if (readPostedKey() === postedKey) return;

        let fingerprintEventId: string | undefined;

        // Check if fingerprint data is already stored before attempting to collect it again, to avoid unnecessary API calls.
        const storedFingerprintData = await hasStoredFingerprintData();

        if (fingerprintEnabled && !storedFingerprintData) {
          try {
            const result = await getFingerprintData();
            fingerprintEventId = result?.event_id;
          } catch (fpError) {
            console.error('Fingerprint data collection failed:', fpError);
          }
        }

        const response = await post<{ trialBlocked?: boolean }>(
          '/updateTrialMetadata',
          {
            deviceID,
            ...(fingerprintEventId && { fingerprintEventId }),
          },
        );
        writePostedKey(postedKey);
        if (response?.data?.trialBlocked) {
          await queryClient.invalidateQueries(['user']);
        }
      } catch (error) {
        console.error('Failed to update trial metadata:', error);
      }
    },
    {
      // Mirrors the server short-circuit in trial.updateMetadata: only trialing
      // and expired accounts ever enter device enforcement.
      enabled: !!user && (hasFreeTrialPrime || hasExpiredPrime),
    },
  );
};

export default useUpdateTrialMetadata;
